// Use the STM's internal bootloader, with its "DFU mode", to flash.
// "The STM32H723ZGT _does not_ support dual-bank mode for its internal 1MB flash."
// This is used for single-bank products (e.g., SACS power supply) as of 2025-07-16; see https://app.clickup.com/t/86a80rruh
import {
    hidSend,
} from "../hid";
import {
    FlashAction,
    FlashManagementCommand,
    ProgressCb,
    fileToBytes,
    serializeFlashMgmtRequest,
} from "./common";

const DFU_VENDOR_ID = 0x0483;
const DFU_PRODUCT_ID = 0xdf11;
// Patterns for identifying single-bank (DFU) devices by HID product name
const DFU_PRODUCT_PATTERNS: RegExp[] = [
    /sacs power supply/i,
    // Add additional single-bank product names here (match the HID product name string)
    /Castelion Fin Actuator/i,
];

export const enterDFUMode = async ( hid: HIDDevice ): Promise<USBDevice> => {
    const request = serializeFlashMgmtRequest( { command: FlashManagementCommand.END_OF_UPDATE } );
    // Create a fresh ArrayBuffer-backed Uint8Array to satisfy strict BufferSource typing
    const reqBytes = new Uint8Array( request.byteLength );
    reqBytes.set( new Uint8Array( request.buffer, request.byteOffset, request.byteLength ) );
    await hidSend( hid, reqBytes );
    const device = await navigator.usb.requestDevice( {
        filters: [ { vendorId: DFU_VENDOR_ID, productId: DFU_PRODUCT_ID } ],
    } );

    return device;
};

export const productNameSupportsDFU = ( productName: string ): boolean => {
    return DFU_PRODUCT_PATTERNS.some( ( pattern ) => pattern.test( productName ) );
};

export const supportsDFU = ( device: HIDDevice ): boolean => {
    const productName = device.productName ?? "";
    return productNameSupportsDFU( productName );
};

enum DfuRequest {
    Detach,
    Download,
    Upload,
    GetStatus,
    ClearStatus,
    GetState,
    Abort,
}

enum DfuState {
    appIDLE,
    appDETACH,
    dfuIDLE,
    dfuDNLOAD_SYNC,
    dfuDNBUSY,
    dfuDNLOAD_IDLE,
    dfuMANIFEST_SYNC,
    dfuMANIFEST,
    dfuMANIFEST_WAIT_RESET,
    dfuUPLOAD_IDLE,
    dfuERROR,
}

type DfuStatus = {
    bStatus: number,
    bwPollTimeout: number,
    bState: number,
    iString: number,
};

enum DfuBootloaderCommand {
    Get = 0x00,
    SetAddressPointer = 0x21,
    Erase = 0x41,
}
const DFU_CONFIGURATION_VALUE = 1;
const DFU_INTERFACE_INDEX = 0;

const FLASH_BASE_ADDRESS = 0x08000000;

export class FirmwareUpdateDFU {
    private device: USBDevice;
    private callback?: ProgressCb;
    private cstoreOffset?: number;
    private sectorSize?: number;

    constructor( device: USBDevice, callback?: ProgressCb, cstoreOffset?: number, sectorSize?: number ) {
        this.device = device;
        this.callback = callback;
        this.cstoreOffset = cstoreOffset;
        this.sectorSize = sectorSize;
    }

    run = async ( fileOrBytes: File|Uint8Array, target: FlashAction ): Promise<boolean> => {
        if ( target !== FlashAction.FlashExecutable ) throw new Error( "not implemented" );
        await this.initializeDevice();

        try {
            await this.abort();
            await this.eraseMemory();
            await this.setAddressPointer( 0x08000000 );
            await this.flashBinary( fileOrBytes );
            await this.exitToFirmware();
            return true;
        } catch ( e ) {
            console.warn( e );
        }

        return false;
    };

    initializeDevice = async () => {
        await this.device.open();
        await this.device.selectConfiguration( DFU_CONFIGURATION_VALUE );
        await this.device.claimInterface( DFU_INTERFACE_INDEX );
    };

    private exitToFirmware = async (): Promise<void> => {
        // Zero-length download to device
        const _bytesWritten = await this.writeMemory( 0x08000000, new ArrayBuffer(0) );
        await this.getStatus();
        console.info( "Exit to firmware." );
    };

    private flashBinary = async ( fileOrBytes: File|Uint8Array ): Promise<void> => {
        const bytes = ( fileOrBytes instanceof Uint8Array ) ? fileOrBytes : await fileToBytes( fileOrBytes );
        const blockSize = 1024;

        let blockNum = 2;
        for ( let offset = 0; offset < bytes.length; offset += blockSize ) {
            const chunk = bytes.slice( offset, offset + blockSize );
            await this.transferOut( DfuRequest.Download, blockNum++, chunk );
            this.callback?.( offset / bytes.length );
            const status = await this.waitForNotState( DfuState.dfuDNBUSY );
            if ( status.bStatus !== 0x00 || status.bState !== DfuState.dfuDNLOAD_IDLE ) {
                throw new Error( `Failed to flash binary at offset ${ offset } (block ${ offset / blockSize }).\nStatus: 0x${ status.bStatus.toString( 16 ) }\nState: ${ status.bState }` );
            }
        }

        await this.transferOut( DfuRequest.Download, blockNum, new Uint8Array( 0 ) );
        await this.abort();
    };

    private waitForNotState = async ( state: number ) => {
        let status = await this.getStatus();
        while ( status.bState === state ) {
            const curTimeout = status.bwPollTimeout ?? 100;
            await new Promise( resolve => setTimeout( resolve, curTimeout ) );
            status = await this.getStatus();
        }
        return status;
    };

    private transferOut = async ( request: number, value: number, data?: BufferSource ): Promise<USBOutTransferResult> => {
        return await this.device.controlTransferOut({
            requestType: "class",
            recipient: "interface",
            request,
            value,
            index: DFU_INTERFACE_INDEX,
        }, data );
    };

    private transferIn = async ( request: number, value: number, byteLength: number ): Promise<USBInTransferResult> => {
        return await this.device.controlTransferIn({
            requestType: "class",
            recipient: "interface",
            request,
            value,
            index: DFU_INTERFACE_INDEX,
        }, byteLength );
    };

    private getStatus = async (): Promise<DfuStatus> => {
        const GET_STATUS_BYTE_LENGTH = 6;
        const result = await this.transferIn( DfuRequest.GetStatus, 0, GET_STATUS_BYTE_LENGTH );

        if ( result.status !== "ok" || result.data === undefined ) {
            console.warn( result );
            throw new Error( `Failed to get status: ${ result.status }` );
        }

        const dv = result.data;

        return {
            bStatus: dv.getUint8( 0 ),
            bwPollTimeout: dv.getUint32( 1, true ) & 0x00FFFFFF,
            bState: dv.getUint8( 4 ),
            iString: dv.getUint8( 5 ),
        };
    };

    private waitForState = async ( state: number, timeout: number = 1000 ): Promise<void> => {
        let status = await this.getStatus();
        let totalTimeout = 0;
        while ( status.bState !== state ) {
            if ( totalTimeout >= timeout ) {
                throw new Error( `Timeout waiting for state ${ state }` );
            }

            const curTimeout = status.bwPollTimeout ?? 100;
            if ( curTimeout === 0 && status.bState !== state ) {
                console.warn( status );
                throw new Error( `Expected state ${ state }, but got ${ status.bState }` );
            }

            totalTimeout += curTimeout;
            await new Promise( resolve => setTimeout( resolve, curTimeout ) );

            status = await this.getStatus();
        }
    };

    private setAddressPointer = async ( address: number ): Promise<void> => {
        const response = await this.transferOut( DfuRequest.Download, 0, new Uint8Array([
            DfuBootloaderCommand.SetAddressPointer,
            address & 0xFF,
            ( address >> 8 ) & 0xFF,
            ( address >> 16 ) & 0xFF,
            ( address >> 24 ) & 0xFF,
        ]));

        const status = await this.waitForNotState( DfuState.dfuDNBUSY );
        if ( response.status !== "ok" || status.bStatus !== 0x00 || status.bState !== DfuState.dfuDNLOAD_IDLE ) {
            throw new Error( `Failed to set address pointer: 0x${ address.toString( 16 ).padStart( 8, "0" ) }.\nResponse status: ${ response.status }\nStatus: 0x${ status.bStatus.toString( 16 ) }\nState: ${ status.bState }` );
        }

        console.info( `Address pointer -> 0x${ address.toString( 16 ).padStart( 8, "0" ) }` );
    };

    /**
     * Erase the executable flash region, preserving the configuration region
     * (CSTORE, KEYSTORE) if both cstoreOffset and sectorSize are known.
     *
     * STM32H723ZGT flash layout (single-bank, 128KB sectors):
     *   Sector 0: 0x08000000 - 0x0801FFFF
     *   Sector 1: 0x08020000 - 0x0803FFFF
     *   ...
     *   Sector 7: 0x080E0000 - 0x080FFFFF
     *
     * If cstoreOffset and sectorSize are provided, only sectors before cstoreOffset are erased.
     * If either is undefined, falls back to mass erase (erases all flash).
     */
    eraseMemory = async (): Promise<void> => {
        if ( this.cstoreOffset === undefined ) {
            console.warn( "cstoreOffset unknown, performing mass erase (config will be lost)" );
            await this.massErase();
            return;
        }

        if ( this.sectorSize === undefined ) {
            console.warn( "sectorSize unknown (firmware may need update), performing mass erase (config will be lost)" );
            await this.massErase();
            return;
        }

        // Calculate which sectors contain executable code (before cstoreOffset)
        const numExecutableSectors = Math.floor( this.cstoreOffset / this.sectorSize );
        if ( numExecutableSectors === 0 ) {
            throw new Error( `Invalid cstoreOffset: 0x${ this.cstoreOffset.toString( 16 ) } - no executable sectors` );
        }

        console.info( `Erasing ${ numExecutableSectors } sector(s) (sector size: 0x${ this.sectorSize.toString( 16 ) }), preserving config at offset 0x${ this.cstoreOffset.toString( 16 ) }` );

        for ( let i = 0; i < numExecutableSectors; i++ ) {
            const sectorAddress = FLASH_BASE_ADDRESS + ( i * this.sectorSize );
            await this.eraseSector( sectorAddress );
        }

        console.info( `Erased executable flash region (sectors 0-${ numExecutableSectors - 1 }), preserved config region.` );
    };

    private massErase = async (): Promise<void> => {
        const response = await this.transferOut( DfuRequest.Download, 0, new Uint8Array([
            DfuBootloaderCommand.Erase,
        ]));

        const status = await this.waitForNotState( DfuState.dfuDNBUSY );
        if ( response.status !== "ok" || status.bStatus !== 0x00 || status.bState !== DfuState.dfuDNLOAD_IDLE ) {
            throw new Error( `Failed to mass erase memory.\nResponse status: ${ response.status }\nStatus: 0x${ status.bStatus.toString( 16 ) }\nState: ${ status.bState }` );
        }

        console.info( "Mass erased flash." );
    };

    private eraseSector = async ( address: number ): Promise<void> => {
        const response = await this.transferOut( DfuRequest.Download, 0, new Uint8Array([
            DfuBootloaderCommand.Erase,
            address & 0xFF,
            ( address >> 8 ) & 0xFF,
            ( address >> 16 ) & 0xFF,
            ( address >> 24 ) & 0xFF,
        ]));

        const status = await this.waitForNotState( DfuState.dfuDNBUSY );
        if ( response.status !== "ok" || status.bStatus !== 0x00 || status.bState !== DfuState.dfuDNLOAD_IDLE ) {
            throw new Error( `Failed to erase sector at 0x${ address.toString( 16 ).padStart( 8, "0" ) }.\nResponse status: ${ response.status }\nStatus: 0x${ status.bStatus.toString( 16 ) }\nState: ${ status.bState }` );
        }

        console.info( `Erased sector at 0x${ address.toString( 16 ).padStart( 8, "0" ) }` );
    };

    readMemory = async ( address: number, byteLength: number ): Promise<DataView> => {
        await this.setAddressPointer( address );
        await this.abort();
        const response = await this.transferIn( DfuRequest.Upload, 2, byteLength );
        await this.abort();

        if ( response.status !== "ok" || response.data === undefined ) {
            throw new Error( `Failed to read ${ byteLength } bytes from 0x${ address.toString( 16 ).padStart( 8, "0" ) }: ${ response.status }` );
        }

        return response.data;
    };

    private writeMemory = async ( address: number, buffer: ArrayBuffer ): Promise<number> => {
        await this.setAddressPointer( address );
        const response = await this.transferOut( DfuRequest.Download, 2, buffer );
        if ( response.status !== "ok" ) {
            throw new Error( `Failed to write ${ buffer.byteLength } bytes to 0x${ address.toString( 16 ).padStart( 8, "0" ) }.\nResponse status: ${ response.status }` );
        }

        console.info( `Wrote ${ buffer.byteLength } bytes to ${ address.toString( 16 ).padStart( 8, "0" ) }.` );

        return response.bytesWritten;
    };

    private abort = async () => {
        await this.transferOut( DfuRequest.Abort, 0 );
        await this.waitForState( DfuState.dfuIDLE );
    }
}

// Kinda gross, but users can call this function if they get stuck in DFU mode
/* eslint-disable @typescript-eslint/no-explicit-any */
( window as any ).exitDFU = async () => {
    const device = await navigator.usb.requestDevice({
        filters: [{
            vendorId: DFU_VENDOR_ID,
            productId: DFU_PRODUCT_ID,
        }],
    });
    const dfu = new FirmwareUpdateDFU( device );
    console.info( dfu );
    await ( dfu as any ).initializeDevice();
    await ( dfu as any ).abort();
    await ( dfu as any ).nBOOTset( true );
    await ( dfu as any ).exitToFirmware();
}
/* eslint-enable @typescript-eslint/no-explicit-any */
